<template>
  <el-container class="container-main" v-loading="isLoading && !teacherSubjects.length">
    <el-header height="auto" class="page-header">
      <h2 class="title">Teacher-Subject Assignments</h2>
      <p class="subtitle">Manage teacher assignments to classes, sections, and subjects</p>
    </el-header>

    <el-main>
      <el-form :model="form" label-position="top" class="form-container" ref="assignmentForm">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="Teacher" prop="teacher_id">
              <el-select
                v-model="form.teacher_id"
                filterable
                placeholder="Select teacher..."
                style="width: 100%;"
                clearable
                @clear="clearSelection('teacher_id')"
                :loading="isLoadingTeachers"
              >
                <el-option
                  v-for="t in teachers"
                  :key="t.id"
                  :label="t.name"
                  :value="t.id"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="Class" prop="class_id">
              <el-select
                v-model="form.class_id"
                placeholder="Select class..."
                style="width: 100%;"
                clearable
                @change="handleClassChange"
                @clear="clearSelection('class_id')"
                :loading="isLoadingClasses"
              >
                <el-option
                  v-for="c in classes"
                  :key="c.id"
                  :label="c.name"
                  :value="c.id"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="Section" prop="section_id">
              <el-select
                v-model="form.section_id"
                placeholder="Select section..."
                style="width: 100%;"
                clearable
                :disabled="!form.class_id || isLoadingSections"
                @change="handleSectionChange"
                @clear="clearSelection('section_id')"
                :loading="isLoadingSections"
              >
                <el-option
                  v-for="s in sections"
                  :key="s.id"
                  :label="s.name"
                  :value="s.id"
                />
                <template #empty v-if="form.class_id && sections.length === 0 && !isLoadingSections">
                  <p class="el-select-dropdown__empty">No sections available for selected class.</p>
                </template>
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="Subjects" prop="subject_ids">
              <el-select
                v-model="form.subject_ids"
                multiple
                filterable
                placeholder="Select subjects..."
                style="width: 100%;"
                clearable
                collapse-tags
                collapse-tags-tooltip
                :disabled="!form.class_id || !form.section_id || isLoadingSubjects"
                :loading="isLoadingSubjects"
              >
                <el-option
                  v-for="s in availableSubjectsForSelection"
                  :key="s.id"
                  :label="s.name"
                  :value="s.id"
                />
                <template #empty v-if="form.section_id && availableSubjectsForSelection.length === 0 && !isLoadingSubjects">
                   <p class="el-select-dropdown__empty">No subjects available for selected section.</p>
                </template>
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="Study Year" prop="study_year_id">
              <el-select
                v-model="form.study_year_id"
                placeholder="Select study year..."
                style="width: 100%;"
                clearable
                @clear="clearSelection('study_year_id')"
                :loading="isLoadingStudyYears"
              >
                <el-option
                  v-for="y in studyYears"
                  :key="y.id"
                  :label="y.year_study"
                  :value="y.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item>
          <el-button
            type="primary"
            @click="createAssignments"
            class="action-button"
            :loading="isSubmitting"
            :disabled="isSubmitting || !formValid"
            style="width: 100%;"
          >
            {{ isSubmitting ? 'Processing...' : 'Assign Subjects' }}
          </el-button>
        </el-form-item>
      </el-form>

      <el-divider />

      <div class="assignments-list">
        <div class="list-header">
          <h3>Current Assignments</h3>
          <el-tag type="info" size="small">{{ teacherSubjects.length }} total</el-tag>
        </div>

        <div v-if="isLoading && teacherSubjects.length === 0" class="loading-state-list">
           <el-skeleton :rows="5" animated />
        </div>
        <el-empty v-else-if="teacherSubjects.length === 0" description="No assignments found" />
        
        <el-timeline v-else style="margin-top: 20px;">
          <el-timeline-item
            v-for="record in teacherSubjects"
            :key="record.id"
            :timestamp="`${record.classroom?.name} / ${record.section?.name} (${record.study_year?.year_study})`"
            placement="top"
          >
            <el-card class="assignment-card">
              <template #header>
                <div class="card-header">
                  <span>
                    <strong>{{ record.teacher?.name }}</strong>
                    <el-icon style="vertical-align: middle; margin: 0 5px;"><Right /></el-icon>
                    {{ record.subject?.name }}
                  </span>
                  <el-button
                    type="danger"
                    :icon="Delete"
                    circle
                    plain
                    size="small"
                    @click="confirmDeleteRecord(record.id)"
                    :loading="isDeleting === record.id"
                  />
                </div>
              </template>
              <div class="class-info-card">
                Assigned to: {{ record.classroom?.name }} - Section {{ record.section?.name }}
                <br />
                Study Year: {{ record.study_year?.year_study }}
              </div>
            </el-card>
          </el-timeline-item>
        </el-timeline>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Delete, Right } from '@element-plus/icons-vue'; // Import icons

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default {
  components: {
    Delete,
    Right
  },
  data() {
    return {
      teacherSubjects: [],
      teachers: [],
      allSubjects: [],
      classes: [],
      sections: [],
      studyYears: [],
      form: {
        teacher_id: null,
        class_id: null,
        section_id: null,
        study_year_id: null,
        subject_ids: [],
      },
      isLoading: false,
      isLoadingTeachers: false, // For individual select loading states
      isLoadingClasses: false,
      isLoadingSections: false,
      isLoadingSubjects: false,
      isLoadingStudyYears: false,
      isSubmitting: false,
      isDeleting: null,
      Delete, // Make icons available
      Right,
    };
  },
  computed: {
    formValid() {
      return (
        this.form.teacher_id &&
        this.form.subject_ids.length > 0 &&
        this.form.class_id &&
        this.form.section_id &&
        this.form.study_year_id
      );
    },
    availableSubjectsForSelection() {
      return this.allSubjects;
    }
  },
  mounted() {
    this.fetchInitialData();
  },
  methods: {
    async fetchInitialData() {
      this.isLoading = true; // Overall loading for the page/initial data fetch
      this.isLoadingTeachers = true; // Indicate that teachers list is loading
      this.isLoadingClasses = true;  // Indicate that classes list is loading
      this.isLoadingStudyYears = true; // Indicate that study years list is loading
      try {
        const response = await api.get('/assign-subjects/initial-data');
        // The backend returns data under response.data.data
        const initialData = response.data.data;

        this.teachers = initialData.teachers || [];
        this.classes = initialData.classes || [];
        this.studyYears = initialData.study_years || [];
        this.teacherSubjects = initialData.teacher_subjects || [];

      } catch (err) {
        console.error('Fetch initial data error:', err);
        ElMessage.error('Failed to load initial data. ' + (err.response?.data?.message || err.message));
        // Ensure arrays are empty on error to prevent issues with selects
        this.teachers = [];
        this.classes = [];
        this.studyYears = [];
        this.teacherSubjects = [];
      } finally {
        this.isLoading = false;
        this.isLoadingTeachers = false;
        this.isLoadingClasses = false;
        this.isLoadingStudyYears = false;
      }
    },

    async handleClassChange(classId) {
      this.form.section_id = null;
      this.sections = [];
      this.form.subject_ids = [];
      this.allSubjects = [];
      if (!classId) return;

      this.isLoadingSections = true;
      try {
        const response = await api.get(`/classes/${classId}/sections`);
        // Backend for getClassSections: response()->json(['success' => true, 'data' => $sections])
        // So, sections are in response.data.data
        this.sections = response.data.sections || [];
      } catch (err) {
        console.error('Fetch sections error:', err);
        ElMessage.error('Failed to load sections. ' + (err.response?.data?.message || err.message));
        this.sections = [];
      } finally {
        this.isLoadingSections = false;
      }
    },

    async handleSectionChange(sectionId) {
      this.form.subject_ids = [];
      this.allSubjects = [];
      if (!this.form.class_id || !sectionId) return;

      this.isLoadingSubjects = true;
      try {
        const response = await api.get(`/classes/${this.form.class_id}/sections/${sectionId}/subjects`);
        // Backend for getClassSectionSubjects: response()->json(['success' => true, 'data' => $subjects])
        // So, subjects are in response.data.data
        this.allSubjects = response.data.data || [];
      } catch (err) {
        console.error('Fetch subjects error:', err);
        ElMessage.error('Failed to load subjects. ' + (err.response?.data?.message || err.message));
        this.allSubjects = [];
      } finally {
        this.isLoadingSubjects = false;
      }
    },

    clearSelection(field) {
      if (field === 'class_id') {
        this.form.section_id = null;
        this.sections = [];
        this.form.subject_ids = [];
        this.allSubjects = [];
      } else if (field === 'section_id') {
        this.form.subject_ids = [];
        this.allSubjects = [];
      }
      // Add logic for other fields if needed
    },

    async createAssignments() {
      if (!this.formValid) {
        ElMessage.warning('Please fill all required fields and select at least one subject.');
        return;
      }

      this.isSubmitting = true;
      try {
        const payload = {
          teacher_id: this.form.teacher_id,
          class_id: this.form.class_id,
          section_id: this.form.section_id,
          study_year_id: this.form.study_year_id,
          subject_ids: this.form.subject_ids
        };
        const response = await api.post('/assign-subjects', payload);

        ElMessage.success(response.data.message || 'Assignments created successfully!');
        this.resetForm();
        this.fetchInitialData(); // Refresh the list of assignments and potentially other initial data if needed
      } catch (err) {
        console.error('Save error:', err);
        ElMessage.error(err?.response?.data?.message || 'Failed to create assignments.');
      } finally {
        this.isSubmitting = false;
      }
    },

    confirmDeleteRecord(id) {
      ElMessageBox.confirm(
        'Are you sure you want to delete this assignment?',
        'Warning',
        {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning',
        }
      ).then(() => {
        this.deleteRecord(id);
      }).catch(() => {
        // User cancelled
      });
    },

    async deleteRecord(id) {
      this.isDeleting = id;
      try {
        await api.delete(`/assign-subjects/${id}`);
        ElMessage.success('Assignment deleted successfully');
        this.teacherSubjects = this.teacherSubjects.filter(record => record.id !== id);
        // Optionally, call fetchInitialData() if you need to refresh everything or handle complex states
      } catch (err) {
        console.error('Delete error:', err);
        ElMessage.error(err?.response?.data?.message || 'Failed to delete assignment.');
      } finally {
        this.isDeleting = null;
      }
    },

    resetForm() {
      this.form = {
        teacher_id: null,
        class_id: null,
        section_id: null,
        study_year_id: null,
        subject_ids: [],
      };
      this.sections = [];
      this.allSubjects = [];
      if (this.$refs.assignmentForm) {
          this.$refs.assignmentForm.resetFields(); // Element Plus way to reset form validation states
      }
    }
  }
};
</script>

<style scoped>
/* Basic styling - Element Plus handles most component styles */
.container-main {
  max-width: 1000px;
  margin: 2rem auto;
  background-color: var(--el-bg-color-page); /* Adapts to Element Plus theme */
}

.page-header {
  padding: 20px;
  text-align: center;
  border-bottom: 1px solid var(--el-border-color-light);
  margin-bottom: 20px;
}

.title {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--el-text-color-primary);
  margin-bottom: 0.5rem;
}

.subtitle {
  font-size: 1rem;
  color: var(--el-text-color-secondary);
}

.form-container {
  padding: 0 20px 20px 20px; /* Add padding if not using el-card for form */
}

.action-button {
  /* width: 100%; /* Already applied inline for this specific button */
  /* margin-top: 10px; /* el-form-item provides spacing */
}

.assignments-list {
  padding: 0 20px 20px 20px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.list-header h3 {
  font-size: 1.25rem;
  color: var(--el-text-color-primary);
}

.assignment-card {
  margin-bottom: 10px; /* For el-timeline, spacing is managed by timeline itself */
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-weight: 500;
}

.class-info-card {
  font-size: 0.9rem;
  color: var(--el-text-color-secondary);
  line-height: 1.6;
}

.loading-state-list {
    padding: 20px;
}

/* Custom styling for el-select empty state if needed */
.el-select-dropdown__empty {
  padding: 10px 0;
  margin: 0;
  text-align: center;
  color: var(--el-text-color-secondary);
  font-size: 14px;
}

/* Ensure Element Plus dark mode variables are respected if you have one configured */
body.dark .container-main {
  /* Element Plus dark variables should apply automatically */
}

/* Your existing dark theme variables can be mapped to Element Plus CSS variables
   if you need a highly custom theme not covered by Element Plus's built-in dark mode.
   This is an advanced topic involving overriding Element Plus styles.
   For now, this scoped CSS is minimal and assumes Element Plus handles theming.
*/
</style>